/**
 * Parser for General step template data
 */

import {
  setSavedStepData,
  updateStepFormData,
} from '../../data-store/copilotSlice';

import type { ParsedField, TemplateAttribute } from './types';

const parseGeneralStep = (
  attributes: TemplateAttribute[],
  isEditMode = false,
  dispatch?: any
): ParsedField[] => {
  const fields: ParsedField[] = [];

  attributes.forEach((attr) => {
    if (!attr.displayOnUI) {
      return;
    }

    const isRequired = attr.mandatory.toLowerCase() === 'yes';
    const defaultVal = attr.attributeValue ?? '';
    const placeholder = attr.attributeValueExample ?? '';

    if (attr.formatDesc.toLowerCase().includes('textfield')) {
      // Provide user-friendly placeholders and labels instead of translation keys
      let friendlyPlaceholder = placeholder;
      let friendlyLabel = attr.attributeDisplayName;

      if (attr.attributeName === 'securitysystems.systemname') {
        friendlyPlaceholder = 'Enter the application name';
        friendlyLabel = 'Application Name';
      } else if (attr.attributeName === 'endpoints.description') {
        friendlyPlaceholder = '';
        friendlyLabel = 'Application Description';
      } else if (placeholder && placeholder.includes('AOB.')) {
        // Convert translation keys to friendly text
        friendlyPlaceholder = 'Enter value';
      }

      if (friendlyLabel && friendlyLabel.includes('AOB.')) {
        friendlyLabel =
          attr.attributeName.split('.').pop() || attr.attributeName;
      }

      fields.push({
        type: 'TextField' as const,
        label: friendlyLabel,
        attributeName: attr.attributeName,
        payloadAtrributeName: attr.attributeName,
        attributeKey: String(attr.attributeKey),
        attributeDisplayName: attr.attributeDisplayName,
        required: isRequired,
        defaultValue: defaultVal,
        attributeValue: attr.attributeValue,
        placeholder: friendlyPlaceholder,
        kind: attr.formatDesc.toLowerCase().includes('multiline')
          ? 'multiline'
          : 'singleline',
        password: attr.formatDesc.toLowerCase().includes('password'),
        pattern: attr.validationPattern,
        // OPTIMIZATION: Disable application name field in edit mode
        fieldDisabled:
          isEditMode && attr.attributeName === 'securitysystems.systemname',
      });
    } else if (attr.formatDesc.toLowerCase() === 'singleselect dropdown') {
      // Handle toggle group for endpoints.ownerType
      const options = [
        { label: 'User', value: 'User' },
        { label: 'User Group', value: 'User Group' },
      ];

      fields.push({
        type: 'ToggleGroup' as const,
        label: 'Owner',
        attributeDisplayName: attr.attributeDisplayName,
        attributeName: attr.attributeName,
        payloadAtrributeName: attr.attributeName,
        attributeKey: String(attr.attributeKey),
        required: isRequired,
        attributeValue: attr.attributeValue,
        options,
        selection: 1,
        setSelection: () => {},
        size: 'medium',
        isPill: true,
      });
    } else if (
      attr.formatDesc.toLowerCase().includes('singleselect dropdown object')
    ) {
      // Handle dropdown with API calls (like Users/User Groups)
      let friendlyLabel = attr.attributeDisplayName;
      let friendlyPlaceholder = 'Select an option';

      if (attr.attributeName === 'endpoints.ownerkey') {
        friendlyLabel = 'Owner';
        friendlyPlaceholder = 'Select owner';
      } else if (attr.attributeName === 'endpoints.requestowner') {
        friendlyLabel = 'Resource Owner';
        friendlyPlaceholder = 'Select resource owner';
      } else if (friendlyLabel && friendlyLabel.includes('AOB.')) {
        // Convert translation keys to friendly text
        friendlyLabel =
          attr.attributeName.split('.').pop() || attr.attributeName;
      }

      fields.push({
        type: 'Dropdown' as const,
        label: friendlyLabel,
        attributeName: attr.attributeName,
        attributeDisplayName: attr.attributeDisplayName,
        payloadAtrributeName: attr.attributeName,
        attributeKey: String(attr.attributeKey),
        required: isRequired,
        defaultValue: defaultVal,
        attributeValue: attr.attributeValue,
        placeholder: friendlyPlaceholder,
        options: [],
        fieldDisabled: false,
        apiURL: attr.apiURL || undefined,
        // Note: fetchData function will need to be provided by the component
      });
    }
  });

  // In edit mode, populate stepFormData from attributeValue
  if (isEditMode && dispatch) {
    const stepFormData: Record<string, unknown> = {};

    fields.forEach((field) => {
      if (
        field.attributeValue !== undefined &&
        field.attributeValue !== null &&
        field.attributeName
      ) {
        let processedValue = field.attributeValue;

        // Fix for user selection - extract first object from array string
        if (
          field.attributeName === 'endpoints.ownerkey' &&
          typeof field.attributeValue === 'string'
        ) {
          try {
            const parsed = JSON.parse(field.attributeValue) as unknown;

            if (Array.isArray(parsed) && parsed.length > 0) {
              processedValue = JSON.stringify(parsed[0]); // Convert back to string of single object
            }
          } catch {
            // Keep original value if parsing fails
          }
        }

        stepFormData[field.attributeName] = processedValue;
      }
    });

    if (Object.keys(stepFormData).length > 0) {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-call
      dispatch(
        updateStepFormData({ stepName: 'General', formData: stepFormData })
      );
      // eslint-disable-next-line @typescript-eslint/no-unsafe-call
      dispatch(
        setSavedStepData({ stepName: 'General', formData: stepFormData })
      );
    }
  }

  return fields;
};

export default parseGeneralStep;
